import { useCallback } from "react";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Table } from "@/components/ui/Table";
import {
  StayCountLabel,
  StayGuestCell,
  StayLines,
  StayListToolbar,
  StayPitchCell,
  StaySortHeader,
  StayStat,
} from "@/components/bookings/stayList";
import {
  formatPartyLine,
  matchesStayQuery,
  nextStaySort,
  nightsLabel,
  useFilteredStayRows,
  useStayListState,
} from "@/components/bookings/stayListModel";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { bookingStatusLabel } from "@/lib/bookingStatus";
import {
  bookingPitchCodeOrOffen,
  pitchCategoryName,
} from "@/lib/bookingPitchDisplay";
import { arrivalTypeLabel } from "@/lib/arrivalType";
import { resolveGuestName } from "@/lib/guestDisplay";
import { formatLicensePlate, vehicleTypeLabel } from "@/lib/vehicle";
import type { Booking, Pitch, PitchCategory } from "@/types/camping";

function canCheckIn(status: string): boolean {
  return status === "confirmed" || status === "reserved" || status === "requested";
}

function ArrivalActions({
  booking,
  canCheckInToday,
  sending,
  onCheckIn,
  onSendComfortCheckIn,
}: {
  booking: Booking;
  canCheckInToday: boolean;
  sending: boolean;
  onCheckIn: (booking: Booking) => void;
  onSendComfortCheckIn: (booking: Booking) => void;
}) {
  const showComfortMail = canCheckIn(booking.status) && !booking.comfort_check_in_completed;
  const showCheckIn = canCheckInToday && canCheckIn(booking.status);
  if (!showComfortMail && !showCheckIn) {
    return <span className="text-xs text-label-tertiary">—</span>;
  }
  return (
    <div className="flex flex-col items-end gap-1.5">
      {showComfortMail ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={sending}
          title={
            booking.comfort_check_in_email_sent_at
              ? "Comfort-Check-in-Mail erneut senden"
              : "E-Mail für den Comfort Check-in senden"
          }
          onClick={(event) => {
            event.stopPropagation();
            onSendComfortCheckIn(booking);
          }}
        >
          {booking.comfort_check_in_email_sent_at ? "Mail erneut" : "Comfort Check-in"}
        </Button>
      ) : null}
      {booking.comfort_check_in_email_sent_at && !booking.comfort_check_in_completed ? (
        <span className="text-[0.65rem] text-label-tertiary">Mail gesendet</span>
      ) : null}
      {showCheckIn ? (
        <Button
          type="button"
          variant="primary"
          size="sm"
          title="Check-in starten"
          onClick={(event) => {
            event.stopPropagation();
            onCheckIn(booking);
          }}
        >
          Einchecken
        </Button>
      ) : null}
    </div>
  );
}

function vehicleHint(booking: Booking): string | null {
  const arrival = booking.arrival_type ? arrivalTypeLabel(booking.arrival_type) : null;
  const vehicle =
    booking.vehicle_type && booking.vehicle_type !== booking.arrival_type
      ? vehicleTypeLabel(booking.vehicle_type)
      : null;
  const hint = [arrival, vehicle].filter(Boolean).join(" · ");
  return hint || null;
}

export interface ArrivalsTableProps {
  bookings: Booking[];
  pitches: Pitch[];
  categories: PitchCategory[];
  canEdit: boolean;
  canCheckInToday: boolean;
  listKey: string;
  emptyMessage: string;
  onOpen: (booking: Booking) => void;
  onCheckIn: (booking: Booking) => void;
  onSendComfortCheckIn: (booking: Booking) => void;
  sendingComfortCheckInId: string | null;
}

export function ArrivalsTable({
  bookings,
  pitches,
  categories,
  canEdit,
  canCheckInToday,
  listKey,
  emptyMessage,
  onOpen,
  onCheckIn,
  onSendComfortCheckIn,
  sendingComfortCheckInId,
}: ArrivalsTableProps) {
  const { search, setSearch, sort, setSort } = useStayListState(listKey);

  const guestName = useCallback(
    (booking: Booking) => resolveGuestName(booking.guest_id, { booking }),
    [],
  );
  const pitchCode = useCallback(
    (booking: Booking) => bookingPitchCodeOrOffen(booking, pitches),
    [pitches],
  );
  const categoryName = useCallback(
    (booking: Booking) => pitchCategoryName(booking.pitch_category_id, categories),
    [categories],
  );

  const matches = useCallback(
    (booking: Booking, query: string) =>
      matchesStayQuery(query, [
        guestName(booking),
        booking.booking_number,
        pitchCode(booking),
        categoryName(booking),
        booking.license_plate,
        vehicleHint(booking),
        bookingStatusLabel(booking.status),
      ]),
    [categoryName, guestName, pitchCode],
  );

  const sortValue = useCallback(
    (booking: Booking, key: string) => {
      switch (key) {
        case "guest":
          return guestName(booking);
        case "pitch":
          return pitchCode(booking) === "Offen" ? "\uffff" : pitchCode(booking);
        case "stay":
          return booking.departure_date;
        case "status":
          return bookingStatusLabel(booking.status);
        default:
          return booking.booking_number;
      }
    },
    [guestName, pitchCode],
  );

  const visible = useFilteredStayRows(bookings, search, sort, matches, sortValue);
  const filtering = search.trim().length > 0 && visible.length !== bookings.length;
  const openPitchCount = bookings.filter((booking) => !booking.pitch_id).length;
  const onlineCount = bookings.filter((booking) => booking.source === "online").length;
  const requestedCount = bookings.filter((booking) => booking.status === "requested").length;

  return (
    <Table
      className="eh-table--stay"
      isEmpty={visible.length === 0}
      emptyMessage={
        search.trim() && bookings.length > 0 ? "Keine Treffer für die Suche." : emptyMessage
      }
      toolbar={
        <StayListToolbar
          search={search}
          onSearchChange={setSearch}
          placeholder="Gast, Nr., Platz, Kennzeichen…"
          searchLabel="Anreisen durchsuchen"
          stats={
            <>
              <StayCountLabel
                shown={visible.length}
                total={bookings.length}
                singular="Anreise"
                plural="Anreisen"
                filtering={filtering}
              />
              {openPitchCount > 0 ? (
                <StayStat tone="warn">
                  <strong>{openPitchCount}</strong>
                  ohne Platz
                </StayStat>
              ) : null}
              {requestedCount > 0 ? (
                <StayStat tone="warn">
                  <strong>{requestedCount}</strong>
                  {requestedCount === 1 ? "Anfrage" : "Anfragen"}
                </StayStat>
              ) : null}
              {onlineCount > 0 ? (
                <StayStat>
                  <strong>{onlineCount}</strong>
                  Online
                </StayStat>
              ) : null}
            </>
          }
        />
      }
    >
      <thead>
        <tr>
          <StaySortHeader
            label="Gast"
            sortKey="guest"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <StaySortHeader
            label="Platz"
            sortKey="pitch"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <StaySortHeader
            label="Aufenthalt"
            sortKey="stay"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          <StaySortHeader
            label="Status"
            sortKey="status"
            sort={sort}
            onSort={(key) => setSort((current) => nextStaySort(current, key))}
          />
          {canEdit ? (
            <th className="text-right" data-tour="arrivals-actions">
              Aktion
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {visible.map((booking) => {
          const plate = booking.license_plate
            ? formatLicensePlate(booking.license_plate, booking.license_plate_country)
            : null;
          const meta = [booking.booking_number, plate, vehicleHint(booking)]
            .filter(Boolean)
            .join(" · ");
          return (
            <ClickableTableRow
              key={booking.id}
              ariaLabel={`Buchung ${booking.booking_number} öffnen`}
              onActivate={() => onOpen(booking)}
            >
              <td data-label="Gast">
                <StayGuestCell
                  name={guestName(booking)}
                  meta={meta}
                  badges={
                    <>
                      {booking.source === "online" ? (
                        <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[0.65rem] font-semibold text-accent">
                          Online
                        </span>
                      ) : null}
                      {booking.comfort_check_in_completed ? (
                        <span className="rounded-full bg-status-green/15 px-2 py-0.5 text-[0.65rem] font-semibold text-status-green">
                          Comfort Check-in
                        </span>
                      ) : null}
                    </>
                  }
                />
              </td>
              <td data-label="Platz">
                <StayPitchCell
                  code={pitchCode(booking)}
                  category={categoryName(booking)}
                  booking={booking}
                />
              </td>
              <td data-label="Aufenthalt">
                <StayLines
                  primary={`Abreise ${formatIsoDateShort(booking.departure_date)}`}
                  secondary={`${nightsLabel(booking.nights)} · ${formatPartyLine(booking)}`}
                />
              </td>
              <td data-label="Status">
                <StatusBadge status={booking.status} />
              </td>
              {canEdit ? (
                <td className="text-right" data-label="Aktion">
                  <ArrivalActions
                    booking={booking}
                    canCheckInToday={canCheckInToday}
                    sending={sendingComfortCheckInId === booking.id}
                    onCheckIn={onCheckIn}
                    onSendComfortCheckIn={onSendComfortCheckIn}
                  />
                </td>
              ) : null}
            </ClickableTableRow>
          );
        })}
      </tbody>
    </Table>
  );
}
